import React, { useMemo, useState, useCallback } from "react";
import Select from "react-select";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import Loader from "Components/Common/Loader";
import TableContainer from "Components/Common/TableContainer";
import {
  useBulkImportProjectProductAssignmentMutation,
  useBulkPublishProjectProductAssignmentsMutation,
  useCreateProjectProductAssignmentMutation,
  useDeleteProjectProductAssignmentMutation,
  useGetProjectProductAssignmentQuantityHistoryQuery,
  useGetProjectProductAssignmentsByProjectIdQuery,
  usePublishProjectProductAssignmentMutation,
  useUpdateProjectProductAssignmentMutation,
} from "slices/projects/projectSlice";
import { useGetProductsListQuery } from "slices/InventoryManagement/inventoryManagementSlice";
import { getToken, getUserRole } from "helpers/jwt-token-access/auth-token-header";

interface ProjectProductsAssignTabProps {
  projectId: string | undefined;
}

interface ProductAssignment {
  id: number;
  project: number;
  project_name: string;
  product: number;
  product_name: string;
  product_brand_company?: string | null;
  product_model_number?: string | null;
  product_category_name?: string | null;
  product_measurement_name?: string | null;
  product_attributes?: Record<string, unknown> | null;
  maximum_quantity: string | number;
  assigned_quantity: string | number;
  requested_quantity: string | number;
  remaining_quantity: string | number;
  remarks?: string | null;
  status: "draft" | "published";
  is_published: boolean;
  can_delete: boolean;
  created_by_name?: string | null;
}

interface QuantityAudit {
  id: number;
  action: "assigned" | "increased";
  previous_quantity?: string | number | null;
  assigned_quantity: string | number;
  created_at: string;
  created_by_name?: string | null;
}

interface ProductOption {
  value: string;
  label: string;
}

const ProjectProductsAssignTab: React.FC<ProjectProductsAssignTabProps> = ({
  projectId,
}) => {
  const [pageState, setPageState] = useState({
    page: 1,
    pageSize: 10,
    search: "",
  });
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<ProductAssignment | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    product: "",
    maximum_quantity: "",
    remarks: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<number[]>([]);
  const [historyAssignment, setHistoryAssignment] = useState<ProductAssignment | null>(null);

  const { data, isLoading, refetch } = useGetProjectProductAssignmentsByProjectIdQuery(
    {
      projectId: projectId || "",
      page: pageState.page,
      pageSize: pageState.pageSize,
      search: pageState.search,
    },
    { skip: !projectId }
  );

  const assignments: ProductAssignment[] = data?.results ?? [];

  const { data: quantityHistory = [], isFetching: isFetchingQuantityHistory } =
    useGetProjectProductAssignmentQuantityHistoryQuery(historyAssignment?.id ?? 0, {
      skip: !historyAssignment,
    });

  const { data: productListData, isFetching: isFetchingProducts } =
    useGetProductsListQuery({ search: productSearch });

  const productOptions = useMemo(
    () =>
      (productListData?.results ?? productListData ?? []).map((product: any) => ({
        value: String(product.id),
      label: `${product.name}${
        product.brand_company ? ` | Brand: ${product.brand_company}` : ""
      }${product.model_number ? ` | Model: ${product.model_number}` : ""}`,
      })),
    [productListData]
  );

  const [createAssignment, { isLoading: isCreating }] =
    useCreateProjectProductAssignmentMutation();
  const [updateAssignment, { isLoading: isUpdating }] =
    useUpdateProjectProductAssignmentMutation();
  const [deleteAssignment, { isLoading: isDeleting }] =
    useDeleteProjectProductAssignmentMutation();
  const [publishAssignment, { isLoading: isPublishing }] =
    usePublishProjectProductAssignmentMutation();
  const [bulkImportAssignment, { isLoading: isImporting }] =
    useBulkImportProjectProductAssignmentMutation();
  const [bulkPublishAssignments, { isLoading: isBulkPublishing }] =
    useBulkPublishProjectProductAssignmentsMutation();

  React.useEffect(() => {
    setSelectedAssignmentIds([]);
  }, [pageState.page, pageState.search]);

  const resetForm = () => {
    setFormData({ product: "", maximum_quantity: "", remarks: "" });
    setFormErrors({});
    setProductSearch("");
  };

  const openAddModal = () => {
    resetForm();
    setAddModal(true);
  };

  const openEditModal = (assignment: ProductAssignment) => {
    setSelectedAssignment(assignment);
    setFormData({
      product: String(assignment.product),
      maximum_quantity: String(assignment.maximum_quantity),
      remarks: assignment.remarks || "",
    });
    setFormErrors({});
    setEditModal(true);
  };

  const openDeleteModal = (assignment: ProductAssignment) => {
    setSelectedAssignment(assignment);
    setDeleteModal(true);
  };

  const openQuantityHistory = (assignment: ProductAssignment) => {
    setHistoryAssignment(assignment);
    setHistoryModal(true);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.product) errors.product = "Product is required";
    const maxQty = parseFloat(formData.maximum_quantity);
    if (!formData.maximum_quantity || Number.isNaN(maxQty) || maxQty <= 0) {
      errors.maximum_quantity = "Maximum quantity must be greater than 0";
    } else if (
      editModal &&
      selectedAssignment &&
      maxQty < parseFloat(String(selectedAssignment.maximum_quantity))
    ) {
      errors.maximum_quantity = `Maximum quantity cannot be lower than the current assigned quantity (${selectedAssignment.maximum_quantity}).`;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDownloadTemplate = useCallback(async () => {
    const apiBase =
      (typeof window !== "undefined" &&
        (window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL) ||
      "";
    try {
      const token = getToken();
      const userRole = getUserRole();
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (userRole) headers["AccessRole"] = userRole;
      const res = await fetch(
        `${apiBase}organization/api/v1/project-product-assignments/download-template/`,
        { headers, credentials: "same-origin" }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "project_product_assignment_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  }, []);

  const handleSubmit = async () => {
    if (!projectId || !validateForm()) return;

    try {
      if (editModal && selectedAssignment) {
        await updateAssignment({
          id: selectedAssignment.id,
          data: {
            maximum_quantity: formData.maximum_quantity,
            remarks: formData.remarks,
          },
        }).unwrap();
        toast.success("Assigned product updated.");
        setEditModal(false);
      } else {
        await createAssignment({
          project: parseInt(projectId, 10),
          product: parseInt(formData.product, 10),
          maximum_quantity: formData.maximum_quantity,
          remarks: formData.remarks,
        }).unwrap();
        toast.success("Product assigned to project.");
        setAddModal(false);
      }
      setSelectedAssignment(null);
      resetForm();
      refetch();
    } catch (err: any) {
      const message =
        err?.data?.product?.[0] ||
        err?.data?.maximum_quantity?.[0] ||
        err?.data?.detail ||
        err?.data?.error ||
        "Failed to save assigned product.";
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!selectedAssignment?.id) return;
    try {
      await deleteAssignment(selectedAssignment.id).unwrap();
      toast.success("Assigned product removed.");
      setDeleteModal(false);
      setSelectedAssignment(null);
      refetch();
    } catch {
      toast.error("Failed to remove assigned product.");
    }
  };

  const handlePublish = async (assignment: ProductAssignment) => {
    try {
      await publishAssignment(assignment.id).unwrap();
      toast.success(`${assignment.product_name} is now available for purchase requests.`);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to publish assigned product.");
    }
  };

  const draftAssignments = assignments.filter(
    (assignment) =>
      !(assignment.is_published || assignment.status === "published")
  );
  const allDraftAssignmentsSelected =
    draftAssignments.length > 0 &&
    draftAssignments.every((assignment) =>
      selectedAssignmentIds.includes(assignment.id)
    );

  const toggleAssignmentSelection = (assignmentId: number, checked: boolean) => {
    setSelectedAssignmentIds((current) =>
      checked
        ? [...new Set([...current, assignmentId])]
        : current.filter((id) => id !== assignmentId)
    );
  };

  const toggleAllDraftAssignments = (checked: boolean) => {
    const draftIds = draftAssignments.map((assignment) => assignment.id);
    setSelectedAssignmentIds((current) =>
      checked
        ? [...new Set([...current, ...draftIds])]
        : current.filter((id) => !draftIds.includes(id))
    );
  };

  const handleBulkPublish = async () => {
    if (!projectId || selectedAssignmentIds.length === 0) return;
    try {
      const result: any = await bulkPublishAssignments({
        project: projectId,
        assignment_ids: selectedAssignmentIds,
      }).unwrap();
      toast.success(result?.message || "Selected products published.");
      setSelectedAssignmentIds([]);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to publish selected products.");
    }
  };

  const formatAttributes = (attributes?: Record<string, unknown> | null) => {
    if (!attributes || Object.keys(attributes).length === 0) return "—";
    return Object.entries(attributes)
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([key, value]) => `${key}: ${String(value)}`)
      .join(", ");
  };

  const handleBulkImport = async () => {
    if (!projectId || !importFile) return;
    setImportError(null);
    try {
      const result: any = await bulkImportAssignment({
        project: projectId,
        file: importFile,
      });
      if (result?.error) {
        const data = result.error?.data;
        const msg =
          data?.error ||
          (data?.missing_columns
            ? `Missing columns: ${data.missing_columns.join(", ")}`
            : data?.row_errors?.[0]
            ? `Row ${data.row_errors[0].row}: ${data.row_errors[0].error}`
            : "Import failed");
        setImportError(msg);
        return;
      }
      toast.success(result?.data?.message || "Project products imported.");
      setImportModal(false);
      setImportFile(null);
      setImportError(null);
      refetch();
    } catch {
      setImportError("Import failed");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <Input
            type="checkbox"
            aria-label="Select all draft products on this page"
            checked={allDraftAssignmentsSelected}
            disabled={draftAssignments.length === 0 || isBulkPublishing}
            onChange={(event) => toggleAllDraftAssignments(event.target.checked)}
          />
        ),
        id: "bulk_select",
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const assignment = row.original as ProductAssignment;
          const published = assignment.is_published || assignment.status === "published";
          return (
            <Input
              type="checkbox"
              aria-label={`Select ${assignment.product_name}`}
              checked={selectedAssignmentIds.includes(assignment.id)}
              disabled={published || isBulkPublishing}
              onChange={(event) =>
                toggleAssignmentSelection(assignment.id, event.target.checked)
              }
            />
          );
        },
      },
      {
        Header: "Product",
        accessor: "product_name",
        Cell: ({ row }: any) => <span className="fw-semibold">{row.original.product_name}</span>,
      },
      {
        Header: "Brand Name",
        accessor: "product_brand_company",
        Cell: ({ row }: any) => row.original.product_brand_company || "—",
      },
      {
        Header: "Model Number",
        accessor: "product_model_number",
        Cell: ({ row }: any) => row.original.product_model_number || "—",
      },
      {
        Header: "Product Attributes",
        accessor: "product_attributes",
        Cell: ({ row }: any) => (
          <span className="text-muted small">{formatAttributes(row.original.product_attributes)}</span>
        ),
      },
      {
        Header: "Maximum Qty",
        accessor: "maximum_quantity",
      },
      {
        Header: "Assigned Qty",
        accessor: "assigned_quantity",
      },
      {
        Header: "Requested Qty",
        accessor: "requested_quantity",
      },
      {
        Header: "Remaining Qty",
        accessor: "remaining_quantity",
        Cell: ({ row }: any) => {
          const remaining = parseFloat(String(row.original.remaining_quantity || 0));
          return (
            <span className={remaining <= 0 ? "text-danger fw-semibold" : "text-success fw-semibold"}>
              {row.original.remaining_quantity}
            </span>
          );
        },
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: ({ row }: any) => row.original.remarks || "—",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }: any) => {
          const published = row.original.is_published || row.original.status === "published";
          return (
            <span className={`badge ${published ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}>
              {published ? "Published" : "Draft"}
            </span>
          );
        },
      },
      {
        Header: "Created By",
        accessor: "created_by_name",
        Cell: ({ row }: any) => row.original.created_by_name || "Not recorded",
      },
      {
        Header: "Action",
        Cell: ({ row }: any) => {
          const assignment = row.original as ProductAssignment;
          const published = assignment.is_published || assignment.status === "published";
          const canDelete = assignment.can_delete ?? !published;
          return (
          <div className="d-flex gap-2">
            {!published && (
              <Button
                color="success"
                size="sm"
                onClick={() => handlePublish(assignment)}
                disabled={isPublishing}
              >
                {isPublishing ? "Publishing..." : "Publish"}
              </Button>
            )}
            <Button
              color="soft-primary"
              size="sm"
              className="btn-icon"
              onClick={() => openEditModal(row.original)}
            >
              <i className="ri-pencil-line" />
            </Button>
            <Button
              color="soft-info"
              size="sm"
              className="btn-icon"
              onClick={() => openQuantityHistory(assignment)}
              title="View quantity history"
            >
              <i className="ri-history-line" />
            </Button>
            <Button
              color="soft-danger"
              size="sm"
              className="btn-icon"
              onClick={() => openDeleteModal(assignment)}
              disabled={!canDelete}
              title={canDelete ? "Delete draft assignment" : "Published assignments cannot be deleted"}
            >
              <i className="ri-delete-bin-line" />
            </Button>
          </div>
          );
        },
      },
    ],
    [
      allDraftAssignmentsSelected,
      draftAssignments.length,
      isBulkPublishing,
      isPublishing,
      selectedAssignmentIds,
    ]
  );

  if (!projectId) return null;

  return (
    <>
      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">Products Assign</h5>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {selectedAssignmentIds.length > 0 && (
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleBulkPublish}
                    disabled={isBulkPublishing}
                  >
                    {isBulkPublishing
                      ? "Publishing..."
                      : `Publish selected (${selectedAssignmentIds.length})`}
                  </Button>
                )}
                <Input
                  type="search"
                  placeholder="Search assigned products..."
                  value={pageState.search}
                  onChange={(e) =>
                    setPageState((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="form-control form-control-sm"
                  style={{ maxWidth: "260px" }}
                />
                <Button color="info" size="sm" outline onClick={() => setImportModal(true)}>
                  <i className="ri-upload-2-line align-bottom me-1" />
                  Bulk Import
                </Button>
                <Button color="primary" size="sm" onClick={openAddModal}>
                  <i className="ri-add-line align-bottom me-1" />
                  Assign Product
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <Loader />
              ) : (
                <TableContainer
                  columns={columns}
                  data={assignments}
                  isPagination={false}
                  isGlobalFilter={false}
                  customPageSize={pageState.pageSize}
                  isServerPagination
                  serverPaginationProps={data}
                  setPageState={setPageState}
                  tableClass="align-middle table-nowrap mb-0"
                  theadClass="table-light"
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={importModal} toggle={() => setImportModal(false)} centered>
        <ModalHeader toggle={() => setImportModal(false)}>
          Bulk Import Assigned Products
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="small">
            Upload a CSV or Excel file with columns: <strong>Brand Name</strong> (optional),{" "}
            <strong>Model Number</strong>, <strong>Maximum Quantity</strong>,{" "}
            <strong>Remarks</strong> (optional).
          </Alert>
          <div className="mb-3">
            <Button color="link" className="p-0" onClick={handleDownloadTemplate}>
              <i className="ri-download-2-line align-bottom me-1" />
              Download sample template
            </Button>
          </div>
          {importError && (
            <Alert color="danger" className="small">
              {importError}
            </Alert>
          )}
          <FormGroup>
            <Label for="project_product_import_file">Select File</Label>
            <Input
              id="project_product_import_file"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e: any) => {
                setImportFile(e.target.files?.[0] || null);
                setImportError(null);
              }}
            />
          </FormGroup>
          <div className="d-flex justify-content-end gap-2">
            <Button color="light" onClick={() => setImportModal(false)}>
              Cancel
            </Button>
            <Button color="primary" disabled={!importFile || isImporting} onClick={handleBulkImport}>
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={addModal || editModal}
        toggle={() => {
          setAddModal(false);
          setEditModal(false);
          setSelectedAssignment(null);
          resetForm();
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            setAddModal(false);
            setEditModal(false);
            setSelectedAssignment(null);
            resetForm();
          }}
        >
          {editModal ? "Edit Assigned Product" : "Assign Product"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Product *</Label>
            <Select
              classNamePrefix="select"
              options={productOptions}
              value={productOptions.find((option: ProductOption) => option.value === formData.product) || null}
              onInputChange={(value: string) => setProductSearch(value)}
              onChange={(option: ProductOption | null) =>
                setFormData((prev) => ({
                  ...prev,
                  product: option?.value || "",
                }))
              }
              isLoading={isFetchingProducts}
              isDisabled={editModal}
              placeholder="Search and select product..."
            />
            {formErrors.product && (
              <div className="invalid-feedback d-block">{formErrors.product}</div>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="maximum_quantity">Maximum Quantity *</Label>
            <Input
              id="maximum_quantity"
              type="number"
              min={
                editModal && selectedAssignment
                  ? String(selectedAssignment.maximum_quantity)
                  : "0.01"
              }
              step="0.01"
              value={formData.maximum_quantity}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  maximum_quantity: e.target.value,
                }))
              }
              invalid={!!formErrors.maximum_quantity}
            />
            <FormFeedback>{formErrors.maximum_quantity}</FormFeedback>
            {editModal && selectedAssignment && (
              <small className="text-muted">
                The quantity can only be increased. Current assigned maximum: {selectedAssignment.maximum_quantity}.
              </small>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="remarks">Remarks</Label>
            <Input
              id="remarks"
              type="textarea"
              rows={3}
              value={formData.remarks}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  remarks: e.target.value,
                }))
              }
            />
          </FormGroup>
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="light"
              onClick={() => {
                setAddModal(false);
                setEditModal(false);
                setSelectedAssignment(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={isCreating || isUpdating}>
              {isCreating || isUpdating
                ? editModal
                  ? "Updating..."
                  : "Assigning..."
                : editModal
                ? "Update"
                : "Assign"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={historyModal}
        toggle={() => {
          setHistoryModal(false);
          setHistoryAssignment(null);
        }}
        centered
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            setHistoryModal(false);
            setHistoryAssignment(null);
          }}
        >
          Quantity Audit Log{historyAssignment ? ` - ${historyAssignment.product_name}` : ""}
        </ModalHeader>
        <ModalBody>
          {isFetchingQuantityHistory ? (
            <Loader />
          ) : quantityHistory.length === 0 ? (
            <p className="text-muted mb-0">No quantity changes have been recorded for this product.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Status</th>
                    <th>Previous Qty</th>
                    <th>Assigned Qty</th>
                    <th>Updated By</th>
                    <th>Updated At</th>
                  </tr>
                </thead>
                <tbody>
                  {(quantityHistory as QuantityAudit[]).map((audit) => (
                    <tr key={audit.id}>
                      <td>{audit.action === "assigned" ? "Assigned" : "Quantity Increased"}</td>
                      <td>{audit.previous_quantity ?? "-"}</td>
                      <td>{audit.assigned_quantity}</td>
                      <td>{audit.created_by_name || "Not recorded"}</td>
                      <td>{new Date(audit.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={deleteModal}
        toggle={() => {
          setDeleteModal(false);
          setSelectedAssignment(null);
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            setDeleteModal(false);
            setSelectedAssignment(null);
          }}
        >
          Remove Assigned Product
        </ModalHeader>
        <ModalBody>
          <p>
            Remove <strong>{selectedAssignment?.product_name}</strong> from this
            project assignment list?
          </p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="light"
              onClick={() => {
                setDeleteModal(false);
                setSelectedAssignment(null);
              }}
            >
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProjectProductsAssignTab;
